import * as React from "react";
import { useDispatch } from "@teamprevent/admin-redux";
import * as moment from "moment";
import { Button, Divider, Form, Label, Modal } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { toast } from "@teamprevent/common/toasts";
import {
    useNotInThePastValidator,
    useTimeHasAlreadyPassed,
    useValidateStartBeforeEndDate,
    useValidateStartEndDateSameDay
} from "@teamprevent/common/customHooks";
import { BookingTypeName, BookingTypes } from "@teamprevent/common/appointment/models";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { validators } from "not-valid";

import { ValidationResultType } from "not-valid/bin/results";

import { BlockClinicModel, ClinicDetails } from "../models";
import { blockClinic } from "../actions";

interface Props {
    clinic: ClinicDetails;
}

export const BlockClinicButton: React.FunctionComponent<Props> = (props: Props) => {
    const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);
    const [submitted, setSubmitted] = React.useState<boolean>(false);

    const [notes, setNotes] = React.useState<string>("");
    const [bookingTypeId, setBookingTypeId] = React.useState<string>("");

    const [blockStartTime, setBlockStartTime] = React.useState<moment.Moment>(props.clinic?.start);
    const [isBlockStartTimeValid, setIsBlockStartTimeValid] = React.useState<boolean>(true);

    const [blockEndTime, setBlockEndTime] = React.useState<moment.Moment>(props.clinic?.end);
    const [isBlockEndTimeValid, setIsBlockEndTimeValid] = React.useState<boolean>(true);

    const startTimeHasAlreadyPassed = useTimeHasAlreadyPassed(props.clinic?.start);
    const endTimeHasAlreadyPassed = useTimeHasAlreadyPassed(props.clinic?.end);
    const diaryBlockingImprovements = getFeatureToggles()[FeatureToggleNames.DiaryBlockingImprovements];
    const blockButtonDisabled = diaryBlockingImprovements ? endTimeHasAlreadyPassed : startTimeHasAlreadyPassed;

    const notInThePastValidator = useNotInThePastValidator();
    const notesLengthValidator = validators.validLength({ max: 140, message: "Max 140 characters" });

    const bookingTypeOptions = React.useMemo(() => {
        const options = BookingTypes.map(t => ({ text: t, value: t }));

        return options.sort((a, b) => a.text.localeCompare(b.text));
    }, []);

    const showNotesField = React.useCallback(() => {
        return bookingTypeId !== "";
    }, [bookingTypeId]);

    const isOverlappingWithAnyChildBooking = React.useCallback((model: BlockClinicModel) => {
        const childAppointments = props.clinic.appointments;
        if (!childAppointments || childAppointments.length === 0) {
            return false;
        }

        return childAppointments.some(ca => {
            return ca.start && ca.end && ca.start < model.end && model.start < ca.end;
        });
    }, [props.clinic.appointments]);

    const isDurationForAnnualLeaveValid = (model: BlockClinicModel) => {
        const typeId = model.bookingTypeId;

        if (!typeId) {
            return true;
        }

        const startTime = model.start;
        const endTime = model.end;

        if (typeId !== BookingTypeName.annualLeave ||
            !startTime ||
            !endTime) {
            return true;
        }

        const isValid = endTime.diff(startTime, "hours") >= 1 && endTime.diff(startTime, "hours") <= 24;
        return isValid;
    };

    const onEdit = React.useCallback(() => setIsEditModalOpen(true), []);

    const onCancel = React.useCallback(
        () => {
            setIsEditModalOpen(false);
            setSubmitted(false);
        },
        []
    );

    const dispatch = useDispatch();
    const onSave = React.useCallback(
        () => {
            setSubmitted(true);

            if ((!notes && !bookingTypeId)
                || !useValidateStartBeforeEndDate(blockStartTime, blockEndTime) // eslint-disable-line react-hooks/rules-of-hooks
                || !useValidateStartEndDateSameDay(blockStartTime, blockEndTime)) { // eslint-disable-line react-hooks/rules-of-hooks
                return;
            }

            const model: BlockClinicModel = {
                notes,
                start: blockStartTime ?? props.clinic.start,
                end: blockEndTime ?? props.clinic.end,
                bookingTypeId
            };

            if (isOverlappingWithAnyChildBooking(model)) {
                toast.info("Block start or end time overlaps with already booked appointments");
                return;
            }

            if (!isDurationForAnnualLeaveValid(model)) {
                toast.info("If booking type is Annual Leave, the duration between start and end times should be at least 1 hour");
                return;
            }

            if (notes) {
                const notesLengthValidationResult = notesLengthValidator(notes);
                if (notesLengthValidationResult.type !== ValidationResultType.Pass) {
                    return;
                }
            }

            if (bookingTypeId === BookingTypeName.other && !notes) {
                return;
            }

            dispatch(blockClinic(props.clinic.id, model));
            setIsEditModalOpen(false);
        },
        [notes, bookingTypeId, blockStartTime, blockEndTime, props.clinic.start, props.clinic.end, props.clinic.id,
            isOverlappingWithAnyChildBooking, dispatch, notesLengthValidator]
    );

    const onNotesChange = React.useCallback((value: string) => setNotes(value), []);

    const onBookingTypeIdChanged = React.useCallback((value: string) => setBookingTypeId(value), []);

    const onBlockStartTimeChange = React.useCallback(
        (value: moment.Moment, isValid: boolean) => {
            if (!isValid) {
                setIsBlockStartTimeValid(false);
            }
            setBlockStartTime(value && value.utc(true));
        },
        []
    );

    const onBlockEndTimeChange = React.useCallback(
        (value: moment.Moment, isValid: boolean) => {
            if (!isValid) {
                setIsBlockEndTimeValid(false);
            }
            setBlockEndTime(value && value.utc(true));
        },
        []
    );

    const startNotBeforeEndError = !useValidateStartBeforeEndDate(blockStartTime, blockEndTime) ?
        <Label basic color="red" pointing content="The start time must be before the end" /> : null;

    const notSameDayError = !useValidateStartEndDateSameDay(blockStartTime, blockEndTime) ?
        <Label basic color="red" pointing content="The start time must be the same day as the end" /> : null;

    return (
        <>
            <Modal open={isEditModalOpen}>
                <Modal.Header content="Block Clinic" />
                <Modal.Content>
                    <Form>
                        <Input.Dropdown
                            inline
                            required
                            label="Booking Type"
                            value={bookingTypeId}
                            showErrors={submitted}
                            options={bookingTypeOptions}
                            onChange={onBookingTypeIdChanged}
                        />
                        { showNotesField() &&
                            <>
                                <Input.Text
                                    label="Notes"
                                    onChange={onNotesChange}
                                    value={notes}
                                    required={bookingTypeId === BookingTypeName.other}
                                    showErrors={submitted}
                                    validation={[notesLengthValidator]}
                                />
                            </>
                        }
                        <Divider hidden />
                        <Input.Datetime
                            label="Block Start Time"
                            onChange={onBlockStartTimeChange}
                            value={blockStartTime}
                            validation={[notInThePastValidator]}
                            showErrors={submitted || !isBlockStartTimeValid}
                        />
                        {startNotBeforeEndError}
                        <Divider hidden />
                        <Input.Datetime
                            label="Block End Time"
                            onChange={onBlockEndTimeChange}
                            value={blockEndTime}
                            validation={[notInThePastValidator]}
                            showErrors={submitted || !isBlockEndTimeValid}
                        />
                        {notSameDayError}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={onCancel} />
                    <Button content="Save" onClick={onSave} />
                </Modal.Actions>
            </Modal>
            <Button content="Block" onClick={onEdit} disabled={blockButtonDisabled} />
        </>
    );
};
